import { PAGINATION_SIZE } from '@constants/index';
import CloseIcon from '@mui/icons-material/Close';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import {
  Box,
  IconButton,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import EmptyMessage from '@shared/components/EmptyMessage';
import Search from '@shared/components/Search';
import Dimmer from '@shared/components/ui/Dimmer';
import clsx from 'clsx';
import React, { ChangeEvent, MouseEvent, useEffect } from 'react';
import { CSVLink as ReactCSVLink } from 'react-csv';
import Highlighter from 'react-highlight-words';
import * as Yup from 'yup';
import '../table/TableView.scss';
import { TablePaginationActions } from './components';
import {
  CSVLinkProps,
  ColumnSearch,
  ErrorField,
  HighlighterProps,
  OrderBy,
  SearchData,
  TableRowData,
  TableViewProps,
} from './interfaces';
import {
  escapeRegExpForHighlight,
  formatCellValueForSearch,
  getHeaderColumnReactKey,
  getTableRowReactKey,
  renderHeaderLabelContent,
} from '../table/tableViewHelpers';
import { getComparator, stableSort } from '../table/tableSortUtils';

const CSVLink = ReactCSVLink as React.ComponentType<CSVLinkProps>;
const HighlighterComponent: React.FC<HighlighterProps> = Highlighter as any;

const useStyles = makeStyles(() => ({
  visuallyHidden: {
    border: 0,
    clip: 'rect(0 0 0 0)',
    height: 1,
    margin: -1,
    overflow: 'hidden',
    padding: 0,
    position: 'absolute',
    top: 20,
    width: 1,
  },
}));

export const TableViewV2: React.FC<TableViewProps> = ({
  rowProps,
  onSearch,
  csvExport,
  pagination,
  headers = [],
  rows = [],
  mapRowData = (row) => row,
  columnsSearch = [],
  isLoading = false,
  stickyHeader = false,
  dense = false,
  maxHeight,
  headerSx,
}) => {
  const classes = useStyles();
  const [orderBy, setOrderBy] = React.useState<OrderBy>({});
  const [searchData, setSearchData] = React.useState<SearchData>({});
  const [rowData, setRowData] = React.useState<TableRowData[] | null>(null);
  const [search, setSearch] = React.useState<string>('');
  const [loading, setLoading] = React.useState<boolean>(false);
  const [columnName, setColumnName] = React.useState<string>('');
  const [tempValues, setTempValues] = React.useState<SearchData>({});
  const inputRefs = React.useRef<Record<string, HTMLInputElement | null>>({});
  const debounceTimer = React.useRef<NodeJS.Timeout | null>(null);

  const [page, setPage] = React.useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = React.useState<number>(PAGINATION_SIZE);
  const [error, setError] = React.useState<ErrorField[]>(
    columnsSearch.map((item) => ({
      name: item.name,
      error: false,
      helperText: '',
    }))
  );

  const handleInputChange = (name: string, value: string, column: ColumnSearch): void => {
    setTempValues((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(() => {
      setSearchData((prev) => ({
        ...prev,
        [name]: value,
      }));
      onSearchColumn(name, column, value);
    }, 300);
  };

  const filterRows = (value: string, rowList: TableRowData[], nameColumn?: string): void => {
    const searchValue = String(value).toUpperCase();

    if (!rowList) return;

    const newRow = rowList.reduce<TableRowData[]>((acc, item) => {
      const mappedItem = mapRowData(item);

      if (!nameColumn) {
        acc.push(mappedItem);
        return acc;
      }

      const cellValue = mappedItem[nameColumn];
      if (formatCellValueForSearch(cellValue).includes(searchValue)) {
        acc.push(mappedItem);
      }

      return acc;
    }, []);

    setRowData(newRow);
  };

  useEffect(() => {
    setRowsPerPage(rowsPerPage || PAGINATION_SIZE);
  }, []);

  useEffect(() => {
    setLoading(false);
    filterRows(search, rows, columnName);
  }, [rows]);

  useEffect(() => {
    pagination && setPage(pagination?.pageNumber);
  }, [pagination?.pageNumber]);

  const createSortHandler = (property: string) => {
    const currentOrder = orderBy[property];
    const newOrderBy = { ...orderBy };

    if (currentOrder === 'asc') {
      newOrderBy[property] = 'desc';
    } else if (currentOrder === 'desc') {
      delete newOrderBy[property];
    } else {
      newOrderBy[property] = 'asc';
    }

    setOrderBy(newOrderBy);
    setRowData(stableSort(rowData || [], getComparator(newOrderBy[property] || 'asc', property)));
  };

  const onChangeSearch = (value = '') => {
    const searchValue = value.toUpperCase();
    setSearch(value);
    setLoading(true);
    filterRows(searchValue, rows, columnName);
    setLoading(false);
  };

  const handleChangeRowsPerPage = (event: ChangeEvent<HTMLInputElement>): void => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setPage(0);
    setLoading(true);
    pagination?.handleChangeRowsPerPage?.(0, newRowsPerPage);
    setRowsPerPage(newRowsPerPage);
  };

  const handleChangePage = (_event: MouseEvent<HTMLButtonElement> | null, newPage: number): void => {
    setLoading(true);
    setPage(newPage);
    pagination?.handleChangePage?.(newPage, rowsPerPage);
  };

  const validationSchema = Yup.string().matches(/^[^\\]*$/, 'Caracter inválido.');

  const onSearchColumn = (name: string, column: ColumnSearch | null, value = '') => {
    validationSchema
      .validate(value)
      .then(() => {
        handleSetError(name);
        if (value === '+') {
          value = '';
          return;
        }
        const newData = searchData;
        newData[name] = value;
        setSearchData(newData);

        onChangeSearch(value?.trim() ?? '');
        setColumnName(name);
        column?.onSearch?.(name, value?.trim() ?? '');
      })
      .catch((err) => {
        handleSetError(name, true, err.message);
      });
  };

  const handleSetError = (nameField: string, error = false, text = '') => {
    setError((prevFields) =>
      prevFields.map((field) => (field.name === nameField ? { ...field, error: error, helperText: text } : field))
    );
  };

  const handleClear = (name: string) => {
    const ref = inputRefs.current[name];
    if (ref) {
      ref.value = '';
    }
    setTempValues((prev) => ({
      ...prev,
      [name]: '',
    }));

    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    setSearchData((prev) => ({
      ...prev,
      [name]: '',
    }));
    onSearchColumn(name, null, '');
  };
  const getSearchSection = () => {
    return (columnsSearch && (<div
      className={clsx('table-search-content', {
        'has-padding': columnsSearch.length > 0,
      })}>
      {columnsSearch.map((item) => (
        item.component || (
          <TextField
            key={item.name}
            variant="outlined"
            label={item.label}
            inputRef={(el) => (inputRefs.current[item.name] = el)}
            value={tempValues[item.name] ?? ''}
            onChange={(event) => {
              const currentValue = event.target.value;
              const newValue = currentValue.replace(/^\s+/, '');

              if (newValue !== searchData[item.name]) {
                handleInputChange(item.name, newValue, item);
              }
            }}
            slotProps={{
              input: {
                style: {
                  backgroundColor: 'white',
                  borderRadius: '12px',
                },
                endAdornment: tempValues[item.name] ? (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => {
                        handleClear(item.name);
                        onSearchColumn(item.name, item, '');
                      }}
                      onMouseDown={() => {
                        handleClear(item.name);
                        onSearchColumn(item.name, item, '');
                      }}>
                      <CloseIcon />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              },
            }}
            error={error.find((e) => e.name === item.name)?.error || false}
            helperText={error.find((e) => e.name === item.name)?.helperText ?? ''}
          />
        )
      ))}
    </div>));
  };

  useEffect(() => {
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, []);

  return (
    <div className="table-view-content">
      <Dimmer active={loading || isLoading}>
        {getSearchSection()}
        <TableContainer
          component={Box}
          style={{ borderRadius: '8px', background: 'white', borderColor: 'unset' }}
          sx={{
            ...(maxHeight !== undefined && { maxHeight }),
            ...(dense && {
              '& .MuiTableCell-body': {
                fontSize: '0.8125rem',
                paddingTop: '6px',
                paddingBottom: '6px',
              },
            }),
          }}>
          <Table aria-label="simple table" stickyHeader={stickyHeader}>
            <TableHead>
              {(onSearch || csvExport) && (
                <TableRow>
                  <TableCell colSpan={headers.length} align="center">
                    {onSearch && <Search onChangeSearch={onChangeSearch} />}
                    {csvExport && (
                      <span>
                        <CSVLink
                          filename={`${new Date().toLocaleDateString()}.csv`}
                          data={rowData || []}
                          headers={headers
                            ?.filter((item) => item.csv)
                            .map(
                              (item) =>
                              (({
                                label: typeof item.label === 'string' ? item.label : String(item.label),
                                key: item.id
                              }) as { label: string; key: string })
                            )}
                          uFEFF>
                          <IconButton>
                            <SaveAltIcon />
                          </IconButton>
                        </CSVLink>
                        CSV
                      </span>
                    )}
                  </TableCell>
                </TableRow>
              )}
              <TableRow>
                {headers?.map((item, index) => {
                  const { label, renderHeaderCell, HeaderCellProps, id, isSortable, align = 'left' } = item;
                  if (renderHeaderCell) {
                    const headerCell = renderHeaderCell(item, index, headers);
                    return React.isValidElement(headerCell) ? headerCell : null;
                  }

                  const sortLabelContent = (
                    <>
                      {renderHeaderLabelContent(label)}
                      {orderBy[id] ? (
                        <span className={classes.visuallyHidden}>
                          {orderBy[id] === 'desc' ? 'sorted descending' : 'sorted ascending'}
                        </span>
                      ) : null}
                    </>
                  );

                  return (
                    <TableCell
                      className="table-view-header"
                      align={align}
                      {...HeaderCellProps}
                      sx={{
                        ...(headerSx as any),
                        ...((HeaderCellProps as any)?.sx ?? {}),
                      }}
                      key={getHeaderColumnReactKey(item, index)}>
                      {isSortable ? (
                        <TableSortLabel
                          active={!!orderBy[id]}
                          direction={orderBy[id] === 'desc' ? 'desc' : 'asc'}
                          onClick={() => createSortHandler(id)}>
                          {sortLabelContent}
                        </TableSortLabel>
                      ) : renderHeaderLabelContent(label)}
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>
            <TableBody>
              {!rowData?.length && (
                <TableRow>
                  <TableCell colSpan={headers.length} align="center" sx={{ borderBottom: 'unset' }}>
                    <EmptyMessage />
                  </TableCell>
                </TableRow>
              )}
              {!!rowData?.length &&
                rowData?.map((row, rowIndex) => {
                  return (
                    <TableRow key={getTableRowReactKey(row, rowIndex)} hover {...rowProps}>
                      {headers.map((header, colIndex) => {
                        const { renderRowCell, id: columnId, rowCellProps, isNumber } = header;
                        const columnKey = getHeaderColumnReactKey(header, colIndex);
                        if (renderRowCell) {
                          return (
                            <TableCell
                              className="table-view-cell"
                              align={isNumber ? 'right' : 'left'}
                              {...rowCellProps}
                              key={columnKey}
                              sx={{
                                whiteSpace: 'normal',
                                wordBreak: 'break-word',
                                verticalAlign: 'middle',
                                overflow: 'visible',
                                padding: '8px',
                              }}>
                              {React.isValidElement(renderRowCell(row[columnId], row, header, colIndex)) ? (
                                <>{renderRowCell(row[columnId], row, header, colIndex)}</>
                              ) : null}
                            </TableCell>
                          );
                        }
                        return (
                          <TableCell
                            className="table-view-cell"
                            align={isNumber ? 'right' : 'left'}
                            {...rowCellProps}
                            key={columnKey}>
                            {search &&
                              typeof (String(row[columnId]) === 'string') &&
                              String(columnId) === String(columnName) ? (
                              <HighlighterComponent
                                searchWords={[escapeRegExpForHighlight(search)]}
                                textToHighlight={String(row[columnId])?.replace(/\s+/g, ' ')}
                              />
                            ) : (
                              row[columnId]
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })}
            </TableBody>
            {pagination && !!rowData?.length && (
              <TableFooter>
                <TableRow>
                  <TablePagination
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    count={pagination.rowsTotalCount}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    ActionsComponent={TablePaginationActions}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    sx={{ borderBottom: 'unset' }}
                  />
                </TableRow>
              </TableFooter>
            )}
          </Table>
        </TableContainer>
      </Dimmer>
    </div>
  );
};
